import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';

import { WindowService } from 'src/shared/providers/window.service';

import { DateTime } from 'luxon';

@Component({
  selector: 'tidy-header-datepicker',
  templateUrl: 'header-datepicker.component.html',
  styleUrls: ['./header-datepicker.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class HeaderDatepickerComponent implements OnInit {

  @Input() dateRangeForm: UntypedFormGroup;
  @Input() errorMessage: string;
  @Input() label = '';
  @Input() selectOptions: any[];
  @Input() submitted = false;
  @Input() header = false;
  @Input() rangeOption: string;
  @Input() disabled = false;
  @Input() appearance: 'standard' | 'outline' = 'standard';
  @Input() icon: string;
  @Output() customDateOption: EventEmitter<any> = new EventEmitter<any>();
  @Output() stringDateOption: EventEmitter<string | null> = new EventEmitter<string | null>();
  @ViewChild('startDateInput') startDateInput: ElementRef<HTMLInputElement>;
  @ViewChild('endDateInput') endDateInput: ElementRef<HTMLInputElement>;

  isOpen = false;
  showCustomDatePicker = false;
  prevRangeOption: string = null;
  tempStartDate = '';
  tempEndDate = '';

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private windowService: WindowService
  ) {}

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.el.nativeElement.contains(event.target)) {
      this.isOpen = false;
    }
  }

  ngOnInit() {
    const isSecondaryLayer = localStorage.getItem('isPrimaryLayer') === 'false' && this.windowService.isDesktopRes;
    if (isSecondaryLayer) {
      this.renderer.addClass(this.el.nativeElement, 'secondary-layer');
    }

    this.selectOptions = this.selectOptions ? this.selectOptions : [
      { viewValue: 'All Time', value: 'all_time' },
      { viewValue: 'Today', value: 'today' },
      { viewValue: 'Tomorrow', value: 'tomorrow' },
      { viewValue: 'Last Week', value: 'last_week' },
      { viewValue: 'Next Week', value: 'next_week' },
      { viewValue: 'Last Month', value: 'last_month' },
      { viewValue: 'Next Month', value: 'next_month' },
      { viewValue: 'In the Future', value: 'future' },
      { viewValue: 'In the Past', value: 'past' },
      { viewValue: 'Custom', value: 'custom' }
    ];

    if (this.header && !this.rangeOption) {
      this.rangeOption = 'all_time';
      if (this.dateRangeForm) {
        this.dateRangeForm.patchValue({
          customStringSelect: 'all_time',
          start: null,
          end: null
        });
      }
    }
  }

  toggleDropdown(event: Event): void {
    if (this.disabled) {
      return;
    }
    event.stopPropagation();
    this.isOpen = !this.isOpen;
    this.showCustomDatePicker = false;
  }

  selectOption(option: any, event: Event): void {
    event.stopPropagation();
    this.prevRangeOption = this.rangeOption;
    this.rangeOption = option.value;

    if (option.value === 'custom') {
      this.showCustomDatePicker = true;
      this.tempStartDate = '';
      this.tempEndDate = '';
      setTimeout(() => {
        this.startDateInput?.nativeElement?.focus();
      }, 50);
    } else {
      this.isOpen = false;
      this.showCustomDatePicker = false;

      if (option.value === 'all_time') {
        this.stringDateOption.emit(option.value);
        if (this.dateRangeForm) {
          this.dateRangeForm.patchValue({
            customStringSelect: option.value,
            start: null,
            end: null
          });
        }
      } else if (option.value === 'future' || option.value === 'past') {
        this.stringDateOption.emit(option.value);
        if (this.dateRangeForm) {
          this.dateRangeForm.patchValue({
            customStringSelect: option.value,
            start: null,
            end: null
          });
        }
      } else {
        this.stringDateOption.emit(option.value);
        const timeRange = this.getTimeRange(option.value);
        if (this.dateRangeForm) {
          this.dateRangeForm.patchValue({
            customStringSelect: option.value,
            start: timeRange.start,
            end: timeRange.end
          });
        }
      }
    }
  }

  applyCustomDate(event: Event): void {
    event.stopPropagation();
    if (!this.tempStartDate || !this.tempEndDate) {
      return;
    }

    const startDate = DateTime.fromISO(this.tempStartDate).startOf('day').toISO();
    const endDate = DateTime.fromISO(this.tempEndDate).endOf('day').toISO();

    if (this.dateRangeForm) {
      this.dateRangeForm.patchValue({
        customStringSelect: 'custom',
        start: startDate,
        end: endDate
      });
    }

    this.customDateOption.emit({
      start: startDate,
      end: endDate
    });

    this.isOpen = false;
    this.showCustomDatePicker = false;
  }

  cancelCustomDate(event: Event): void {
    event.stopPropagation();
    this.rangeOption = this.prevRangeOption || 'all_time';
    this.showCustomDatePicker = false;
    this.isOpen = false;
  }

  getDisplayValue(): string {
    if (this.rangeOption === 'custom' && this.dateRangeForm?.value?.start && this.dateRangeForm?.value?.end) {
      const start = DateTime.fromISO(this.dateRangeForm.value.start);
      const end = DateTime.fromISO(this.dateRangeForm.value.end);
      return `${start.toFormat('MMM d')} - ${end.toFormat('MMM d')}`;
    }

    const selectedOption = this.selectOptions?.find(opt => opt.value === this.rangeOption);
    return selectedOption?.viewValue || 'All Time';
  }

  getTimeRange(option: string): { start: string; end: string } {
    const now = DateTime.now();
    let start: DateTime;
    let end: DateTime;

    switch (option) {
      case 'today': {
        start = now.startOf('day');
        end = now.endOf('day');
        break;
      }
      case 'tomorrow': {
        const tomorrow = now.plus({ day: 1 });
        start = tomorrow.startOf('day');
        end = tomorrow.endOf('day');
        break;
      }
      case 'last_week': {
        const lastWeekDay = now.startOf('week').plus({ day: -1 });
        start = lastWeekDay.startOf('week');
        end = lastWeekDay.endOf('week');
        break;
      }
      case 'next_week': {
        const nextWeekDay = now.endOf('week').plus({ day: 1 });
        start = nextWeekDay.startOf('week');
        end = nextWeekDay.endOf('week');
        break;
      }
      case 'last_month': {
        const lastMonthDay = now.startOf('month').plus({ day: -1 });
        start = lastMonthDay.startOf('month');
        end = lastMonthDay.endOf('month');
        break;
      }
      case 'next_month': {
        const nextMonthDay = now.endOf('month').plus({ day: 1 });
        start = nextMonthDay.startOf('month');
        end = nextMonthDay.endOf('month');
        break;
      }
      default: {
        start = now.startOf('day');
        end = now.endOf('day');
      }
    }

    return {
      start: start.toISO(),
      end: end.toISO()
    };
  }
}
